import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import includes from "lodash/includes";
import { useParams, useLocation } from "react-router";
import { getOfferEnrollmentClinicsAction, updateClinicOfferEnrollmentStatusAction } from "actions/ProviderActions";
import ListItem from "components/common/ListItem/ListItem";
import EnrollmentConfirmModal from "./EnrollmentConfirmModal";
import ProviderBase from "components/layout/ProviderBase";
import SpinnerTakeover from "components/common/SpinnerTakeover";
import AccessDeniedPage from "components/clinic/pages/AccessDeniedPage";
import ForeverFeline from "/public/ForeverFeline.pdf"; 
import styles from "./OfferEnrollment.scss";

const MAX_ENROLL = 4;

function OfferEnrollment(props) {
  const {
    clinics,
    isLoading,
    nodeId,
    getOfferEnrollmentClinics,
    updateStatusAction,
    userProfile
  } = props;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [enrolledIds, setEnrolledIds] = useState([]);
  const [isFinalized, setisFinalized] = useState(false);
  const [isEmptyState, setisEmptyState] = useState(false);
  const [isFinalizingEarly, setIsFinalizingEarly] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const params = useParams();
  const isAdmin = !!(includes(location?.pathname, "/admin"));
  const providerId = Number(params.providerId);
  const location = useLocation();

  const hasAccess =
    (userProfile?.providers?.[0]?.id === '1' &&
      userProfile?.roles?.includes("Territory") &&
      userProfile?.roles?.includes("ProviderUser")) ||
      userProfile?.roles?.includes("GLAdmin");

  useEffect(() => {
    const finalizedEarly = localStorage.getItem("isFinalizedEarly") === "true";
    setisFinalized(finalizedEarly);
  }, []);

  useEffect(() => {
    if (nodeId) {
      setShowSpinner(true);
      getOfferEnrollmentClinics(30, nodeId);
      setShowSpinner(false);
    }
  }, [nodeId]);

  useEffect(() => {
    const finalizedEarly = localStorage.getItem("isFinalizedEarly") === "true";
    if (finalizedEarly) {
      setisFinalized(true);
    }

    if (!isLoading) {
      if (clinics.length > 0) {
        setisEmptyState(false);
        const enrolledClinics = clinics
          .filter((clinic) => clinic.serviceStatus === "Enrolled")
          .map((clinic) => clinic.clinicId);
        setEnrolledIds(enrolledClinics);
        if (enrolledClinics.length === MAX_ENROLL) {
          setisFinalized(true);
        }
      } else {
        const timeout = setTimeout(() => {
          setisEmptyState(true);
        }, 1000);

        return () => clearTimeout(timeout);
      }
    }
  }, [isLoading, clinics]);

  const handleEnroll = (clinic) => {
    setSelectedClinic(clinic);
    setIsFinalizingEarly(false);
    setShowConfirmModal(true);
  };

  const handleFinalize = () => {
    setIsFinalizingEarly(true);
    setShowConfirmModal(true);
};

const handleModalConfirm = (data, isFinalizedEarly) => {
  if (isFinalizedEarly) {
      setisFinalized(true);
      localStorage.setItem("isFinalizedEarly", "true");
      setShowConfirmModal(false);
  }
  setShowConfirmModal(false);
  getOfferEnrollmentClinics(30, nodeId);
};

const handleCloseModal = () => {
  setShowConfirmModal(false);
};

if (!hasAccess) {
  useEffect(() => {
    window.location.href = "/no-access/";
  }, []);
  return null;
}

  return (
    <ProviderBase location={location} providerId={providerId} nodeId={nodeId}>
      <div className={styles.enrollmentContainer}>
        <h1>Forever Feline Partnership Offer</h1>
        {!isFinalized && clinics.length > 0 && (
          <p className={styles.description}>
            This pilot program is a partnership between Boehringer Ingelheim and IDEXX to offer a
            cat owner a rebate of $25 when they test and vaccinate their cat for FeLV.
            For more information, please refer to the {" "}
            <a
              href={ForeverFeline}
              target="_blank"
              rel="noopener noreferrer"
            >
              detailer and terms and conditions.
            </a>
          </p>
        )}
        {!isFinalized && clinics.length !== 0 && (
          <div className={styles.progressContainer}>
            <span className={styles.progressText}>
              {enrolledIds.length} of {MAX_ENROLL} clinics selected
            </span>
            <button
              className={styles.finalizeButton}
              onClick={handleFinalize}
              disabled={enrolledIds.length === 0}
            >
              Finalize Enrollment
            </button>
          </div>
        )}

        <div className={styles.listItemContainer}>
          {isLoading ? (
            <div></div>
          ) : isEmptyState ? (
            <div className={styles.emptyState}>
              <p>No clinics available for enrollment.</p>
            </div>
          ) : (isFinalized || enrolledIds.length === MAX_ENROLL) ? (
            <div className={styles.successState}>
                <i className="fa fa-check"></i>
                <div className={styles.successHeader}>Complete</div>
                <div className={styles.successDescription}>
                    You’ve successfully enrolled {enrolledIds.length} clinic{enrolledIds.length > 1 ? 's' : ''}. Greenline customer service will contact your
                    clinics within 24 hours of enrollment to set up the Forever Feline offer.
                </div>
                <div className={styles.successEnrolledClinics}>
                    {enrolledIds.map((id) => {
                        const clinic = clinics.find((clinic) => clinic.clinicId === id);
                        return clinic ? <div key={clinic.clinicId}>{clinic.clinicName}</div> : null;
                    })}
                </div>
            </div>
          ) : (
            clinics.map((clinic) => (
              <ListItem
                key={clinic.clinicId}
                entityId={clinic.clinicId}
                entityName={clinic.clinicName}
                isEnrolled={enrolledIds.includes(clinic.clinicId)}
                onButtonClick={() => handleEnroll(clinic)}
                buttonText={"Enroll"}
              />
            ))
          )}
        </div>

        {showConfirmModal && (
          <EnrollmentConfirmModal
            show={showConfirmModal}
            onClose={handleCloseModal}
            onConfirm={handleModalConfirm}
            clinic={selectedClinic}
            isFinalizingEarly={isFinalizingEarly}
            enrolledClinicCount={enrolledIds.length}
            updateStatusAction={updateStatusAction}
          />
        )}
      </div>
      <SpinnerTakeover show={showSpinner || isLoading} />
    </ProviderBase>
  );
}

const mapStateToProps = (state, ownProps) => {

  const userProfile = state.user.userProfile;
  const currentOrgNode = state.entities.orgNode?.current;
  const params = ownProps?.match?.params || {};
  const providerId = Number(params?.providerId) || null;
  const nodeId = Number(params?.nodeId) || null;

  return {
    clinics: state.entities.offerEnrollment?.clinics || [],
    isLoading: state.entities.offerEnrollment?.isLoading || false,
    nodeId,
    userProfile,
    currentOrgNode,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getOfferEnrollmentClinics: (programServiceId, nodeId) =>
    dispatch(getOfferEnrollmentClinicsAction(programServiceId, nodeId)),
  updateStatusAction: (clinicId, programServiceId, data) =>
    dispatch(updateClinicOfferEnrollmentStatusAction(clinicId, programServiceId, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OfferEnrollment);